(() => {
  'use strict';

  const config = window.theme || { routes: {} };
  const $ = (selector, scope = document) => scope?.querySelector(selector) || null;
  const $$ = (selector, scope = document) => scope ? Array.from(scope.querySelectorAll(selector)) : [];
  const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const cartShell = $('[data-cart-shell]');
  const searchShell = $('[data-search-shell]');
  const productModal = $('[data-product-modal]');
  const toastRegion = $('[data-toast-region]');
  let lastFocus = null;
  let predictiveController = null;
  let judgeReviewCache = [];
  let judgeReviewMedia = [];
  let visibleJudgeReviewCount = 6;
  let judgeSubmissionTimer = null;
  let judgeReviewLoadingTimedOut = false;
  let lockedPageScrollY = 0;

  const pageLockClasses = [
    'overlay-open',
    'drawer-open',
    'modal-open',
    'menu-is-open',
    'filter-open',
    'judge-review-open'
  ];

  const restorePageScroll = (top) => {
    const root = document.documentElement;
    const previousScrollBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    window.scrollTo({ top, left: 0, behavior: 'auto' });
    window.requestAnimationFrame(() => {
      if (previousScrollBehavior) root.style.scrollBehavior = previousScrollBehavior;
      else root.style.removeProperty('scroll-behavior');
    });
  };

  const syncPageScrollLock = () => {
    const shouldLock = pageLockClasses.some((className) => document.body.classList.contains(className));
    const isLocked = document.body.classList.contains('page-scroll-locked');
    if (shouldLock && !isLocked) {
      lockedPageScrollY = window.scrollY;
      document.body.style.top = `-${lockedPageScrollY}px`;
      document.body.classList.add('page-scroll-locked');
      return;
    }
    if (!shouldLock && isLocked) {
      const restoreY = lockedPageScrollY;
      document.body.classList.remove('page-scroll-locked');
      document.body.style.removeProperty('top');
      restorePageScroll(restoreY);
    }
  };

  const pageLockObserver = new MutationObserver(syncPageScrollLock);
  pageLockObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${config.currency || ''}`.trim();
    }
  };

  const announce = (message, type = 'success') => {
    if (!toastRegion) return;
    const item = document.createElement('div');
    item.className = `toast${type === 'error' ? ' is-error' : ''}`;
    item.textContent = message;
    toastRegion.append(item);
    window.setTimeout(() => item.remove(), 3600);
  };

  const resetJudgeReviewSubmitting = (modal, { timedOut = false } = {}) => {
    window.clearTimeout(judgeSubmissionTimer);
    judgeSubmissionTimer = null;
    if (!modal) return;
    modal.classList.remove('is-submitting');
    const submitButton = $('.jdgm-write-review-modal__nav-btn-next.is-submitting', modal);
    if (submitButton) {
      submitButton.classList.remove('is-submitting');
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
      submitButton.textContent = submitButton.dataset.submitLabel || 'Submit Review';
      submitButton.setAttribute('aria-label', submitButton.dataset.submitAriaLabel || 'Next step');
    }
    const backButton = $('.jdgm-write-review-modal__nav-btn-back', modal);
    if (backButton) backButton.disabled = false;
    if (timedOut) {
      announce('The review is taking longer than expected. Please check your connection and try again.', 'error');
    }
  };

  const setJudgeReviewSubmitting = (button) => {
    const modal = button?.closest('.jdgm-write-review-modal');
    if (!modal || modal.classList.contains('is-submitting')) return;
    modal.classList.add('is-submitting');
    button.dataset.submitLabel = button.textContent.trim() || 'Submit Review';
    button.dataset.submitAriaLabel = button.getAttribute('aria-label') || 'Next step';
    button.classList.add('is-submitting');
    button.textContent = 'Submitting…';
    button.setAttribute('aria-label', 'Submitting review');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    const backButton = $('.jdgm-write-review-modal__nav-btn-back', modal);
    if (backButton) backButton.disabled = true;
    window.clearTimeout(judgeSubmissionTimer);
    judgeSubmissionTimer = window.setTimeout(() => {
      resetJudgeReviewSubmitting(modal, { timedOut: true });
    }, 20000);
  };

  const closeJudgeReviewForm = () => {
    resetJudgeReviewSubmitting($('.jdgm-write-review-modal'));
    const bridge = $('[data-judgeme-submission-bridge]');
    bridge?.classList.remove('is-reviewing', 'has-native-form', 'is-submitted');
    bridge?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('judge-review-open');
  };

  const openJudgeReviewForm = () => {
    const bridge = $('[data-judgeme-submission-bridge]');
    if (!bridge) {
      announce('The review form is temporarily unavailable. Please try again.', 'error');
      return;
    }
    bridge.classList.add('is-reviewing');
    bridge.setAttribute('aria-hidden', 'false');
    document.body.classList.add('judge-review-open');
    const openNativeForm = () => {
      const button = $('[data-testid="write-review-button"], .jdgm-write-rev-link', bridge);
      if (!button) return false;
      bridge.classList.add('has-native-form');
      button.click();
      window.setTimeout(() => {
        if (!$('.jdgm-write-review-modal')) {
          closeJudgeReviewForm();
          announce('The review form is temporarily unavailable. Please try again.', 'error');
        }
      }, 2500);
      return true;
    };
    if (openNativeForm()) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (openNativeForm() || Date.now() - startedAt > 5000) {
        window.clearInterval(timer);
        if (Date.now() - startedAt > 5000) {
          closeJudgeReviewForm();
          announce('The review form is still loading. Please try again.', 'error');
        }
      }
    }, 160);
  };

  const judgeReviewImageUrl = (image, width = 1400) => {
    const srcsetCandidates = (image?.getAttribute('srcset') || '')
      .split(',')
      .map((candidate) => candidate.trim().split(/\s+/)[0])
      .filter(Boolean);
    const source = srcsetCandidates.at(-1)
      || image?.currentSrc
      || image?.getAttribute('src')
      || image?.getAttribute('data-src')
      || image?.getAttribute('data-original')
      || image?.closest('a[href]')?.getAttribute('href')
      || '';
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('quality', '88');
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return source;
    }
  };

  const isCampaignReviewPage = document.body.classList.contains('template-product-campaign-lab');
  const campaignReviewRatings = isCampaignReviewPage ? window.CampaignReviewRating : null;

  const readJudgeReview = (review) => {
    const ratingImage = $('.jm-star-rating[aria-label*="out of 5 stars"],img[alt*="out of 5 stars"],[aria-label*="out of 5 stars"],.jdgm-rev__rating', review);
    const ratingText = ratingImage?.getAttribute('aria-label') || ratingImage?.getAttribute('alt') || '';
    const rating = isCampaignReviewPage
      ? (campaignReviewRatings?.readScore(review) ?? null)
      : Math.max(1, Math.min(5, Number.parseFloat(ratingText) || 5));
    const title = $('.jm-review-content__title,.jdgm-rev__title', review)?.textContent?.trim() || '';
    const body = $('.jdgm-review-content__body-content,.jm-review-content__body,.jdgm-rev__body', review)?.textContent?.trim() || '';
    const name = $('.jm-reviewer-info__name,.jdgm-rev__author', review)?.textContent?.trim() || 'Customer';
    const details = $('.jm-reviewer-info__details', review);
    const date = $('time,.jdgm-rev__timestamp', review)?.textContent?.trim()
      || $$('span,p', details)
        .map((item) => item.textContent?.trim())
        .find((text) => /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(text || ''))
      || '';
    const images = $$('img[alt="Review Image"],.jm-review-item__media img,.jm-media-grid img,.jdgm-rev__pic-img', review)
      .map((image) => ({
        src: judgeReviewImageUrl(image, 1800),
        thumb: judgeReviewImageUrl(image, 720)
      }))
      .filter((image) => image.src)
      .filter((image, index, all) => all.findIndex((candidate) => candidate.src === image.src) === index);
    const verified = Boolean($('.jm-verified-buyer-badge,.jdgm-rev__buyer-badge', review));
    if (!body && !title) return null;
    return { rating, title, body, name, date, images, verified };
  };

  const judgeReviewKey = (review) => [
    review.name,
    review.title,
    review.body
  ].map((value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase()).join('::');

  const renderJudgeReviewSummary = (average, reviewCount) => `<div class="pdp-reviews__rating-line" aria-label="${average.toFixed(1)} out of 5 stars, ${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}">
    <span class="pdp-reviews__aggregate-star" aria-hidden="true">★</span>
    <strong class="pdp-reviews__score-value">${average.toFixed(1)}</strong>
    <span class="pdp-reviews__count">${reviewCount} ${reviewCount === 1 ? 'review' : 'reviews'}</span>
  </div>`;

  const syncReviewMediaPreview = (preview) => {
    const track = $('[data-review-header-media]', preview);
    const previous = $('[data-review-media-strip-step="-1"]', preview);
    const next = $('[data-review-media-strip-step="1"]', preview);
    if (!track || !previous || !next) return;
    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const hasOverflow = maximumScroll > 1;
    previous.hidden = !hasOverflow || track.scrollLeft <= 1;
    next.hidden = !hasOverflow || track.scrollLeft >= maximumScroll - 1;
  };

  const setupReviewMediaPreview = (preview) => {
    const track = $('[data-review-header-media]', preview);
    if (!track || preview._reviewMediaReady) return;
    preview._reviewMediaReady = true;
    track.addEventListener('scroll', () => {
      if (track._reviewMediaFrame) return;
      track._reviewMediaFrame = window.requestAnimationFrame(() => {
        track._reviewMediaFrame = null;
        syncReviewMediaPreview(preview);
      });
    }, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const items = $$('[data-open-review-gallery]', track);
      const current = event.target.closest?.('[data-open-review-gallery]');
      const currentIndex = items.indexOf(current);
      if (currentIndex < 0) return;
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : Math.max(0, Math.min(items.length - 1, currentIndex + (event.key === 'ArrowRight' ? 1 : -1)));
      event.preventDefault();
      items[nextIndex]?.focus({ preventScroll: true });
      items[nextIndex]?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    });
    if ('ResizeObserver' in window) {
      preview._reviewMediaObserver = new ResizeObserver(() => syncReviewMediaPreview(preview));
      preview._reviewMediaObserver.observe(track);
    }
  };

  const stepReviewMediaPreview = (target) => {
    const preview = target.closest('[data-review-header-media-preview]');
    const track = $('[data-review-header-media]', preview);
    const firstItem = $('[data-open-review-gallery]', track);
    if (!preview || !track || !firstItem) return;
    const itemWidth = firstItem.getBoundingClientRect().width;
    const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    const step = Number(target.dataset.reviewMediaStripStep || 1);
    track.scrollBy({
      left: step * (itemWidth + gap) * 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  };

  const renderReviewMediaCount = (count) => count > 0 ? `<span class="pdp-review-card__media-count" aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" focusable="false"><rect x="3" y="5" width="14" height="14" rx="2"></rect><path d="m6.5 15 2.8-3 2.2 2 1.7-1.7 2.3 2.7"></path><path d="M7 8.5h.01"></path><path d="M7 3h12a2 2 0 0 1 2 2v12"></path></svg>
    <span>+${count}</span>
  </span>` : '';

  const renderJudgeReviewCard = (review, reviewIndex) => {
    if (isCampaignReviewPage) {
      return `<article class="pdp-review-card" data-judge-review-card="${reviewIndex}">
        ${review.images.length ? `<div class="pdp-review-card__media pdp-review-card__media--gallery">
          ${review.images.map((image, imageIndex) => `<button type="button" data-open-review-detail="${imageIndex}" aria-label="Open customer photo ${imageIndex + 1} of ${review.images.length}"><img src="${escapeHTML(image.thumb)}" alt="" loading="lazy">${imageIndex === 3 && review.images.length > 4 ? `<span>+${review.images.length - 4}</span>` : ''}</button>`).join('')}
        </div>` : ''}
        <div class="pdp-review-card__copy">
          <div class="pdp-review-card__meta">${campaignReviewRatings?.stars(review.rating) || ''}${review.verified ? '<span class="pdp-review-card__verified">Verified purchase</span>' : ''}</div>
          ${review.title ? `<h3>${escapeHTML(review.title)}</h3>` : ''}
          <div class="rte"><p>${escapeHTML(review.body)}</p></div>
          <footer><strong>${escapeHTML(review.name)}</strong>${review.date ? `<time>${escapeHTML(review.date)}</time>` : ''}</footer>
        </div>
      </article>`;
    }

    const reviewerInitial = review.name.trim().charAt(0).toUpperCase() || 'C';
    return `<article class="pdp-review-card pdp-review-card--minimal" data-judge-review-card="${reviewIndex}">
      <div class="pdp-review-card__body">
        <div class="pdp-review-card__copy">
          <div class="pdp-review-card__meta"><span class="pdp-reviews__stars" aria-label="${review.rating} out of 5 stars">★★★★★</span></div>
          <div class="pdp-review-card__reviewer">
            <span class="pdp-review-card__avatar" aria-hidden="true">${escapeHTML(reviewerInitial)}</span>
            <div class="pdp-review-card__reviewer-details">
              <strong class="pdp-review-card__reviewer-name">${escapeHTML(review.name)}</strong>
              ${review.verified || review.date ? `<div class="pdp-review-card__reviewer-meta">${review.verified ? '<span class="pdp-review-card__verified">Verified purchase</span>' : ''}${review.date ? `<time>${escapeHTML(review.date)}</time>` : ''}</div>` : ''}
            </div>
          </div>
          <div class="pdp-review-card__content">
            ${review.title ? `<h3>${escapeHTML(review.title)}</h3>` : ''}
            <div class="rte"><p>${escapeHTML(review.body)}</p></div>
          </div>
        </div>
        ${review.images.length ? `<div class="pdp-review-card__media pdp-review-card__media--gallery">
          <button type="button" data-open-review-detail="0" aria-label="${review.images.length > 1 ? `Open all ${review.images.length} customer photos` : 'Open customer photo'}"><img src="${escapeHTML(review.images[0].thumb)}" alt="" loading="lazy" decoding="async">${renderReviewMediaCount(review.images.length - 1)}</button>
        </div>` : ''}
      </div>
    </article>`;
  };

  const syncJudgeReviews = () => {
    const bridge = $('[data-judgeme-submission-bridge]');
    const section = $('[data-real-reviews-section]');
    const summary = $('[data-real-review-summary]');
    const list = $('[data-real-review-list]');
    const headerMedia = $('[data-review-header-media]', section);
    const headerMediaPreview = $('[data-review-header-media-preview]', section);
    if (!bridge || !section || !summary || !list) return;
    campaignReviewRatings?.refresh();
    const widgetBody = $('.jm-review-widget__body,.jdgm-rev-widg__body', bridge);
    if (!widgetBody) {
      if (!isCampaignReviewPage && judgeReviewLoadingTimedOut) {
        summary.className = 'pdp-reviews__empty-copy pdp-reviews__empty-copy--error';
        summary.innerHTML = '<strong>Reviews are temporarily unavailable</strong><span>Please try again shortly.</span>';
        section.classList.remove('pdp-reviews--loading');
        section.classList.add('pdp-reviews--empty');
        section.setAttribute('aria-busy', 'false');
        judgeReviewMedia = [];
        if (headerMediaPreview) headerMediaPreview.hidden = true;
      }
      return;
    }
    const reviewCountText = $$('.jm-text,.jdgm-rev-widg__summary-text', bridge)
      .map((item) => item.textContent?.trim() || '')
      .find((text) => /^\d+\s+reviews?$/i.test(text))
      || $('.jm-review-widget__header,.jdgm-rev-widg__header', bridge)?.textContent
      || '';
    const countMatch = reviewCountText.match(/(\d+)\s+reviews?/i);
    const nativeReviewCount = Number(countMatch?.[1]);
    let candidates = $$('[data-review-id],[data-review-uuid],.jm-review-card,.jm-review-item,.jdgm-rev', widgetBody);
    if (!candidates.length) {
      candidates = $$('.jm-star-rating[aria-label*="out of 5 stars"],img[alt*="out of 5 stars"]', widgetBody)
        .map((image) => image.closest('article,li,[class*="review"]'))
        .filter((item, index, all) => item && item !== widgetBody && all.indexOf(item) === index);
    }
    const incomingReviews = candidates.map(readJudgeReview).filter(Boolean);
    if (!incomingReviews.length && !judgeReviewCache.length) {
      if (isCampaignReviewPage || !judgeReviewLoadingTimedOut) return;
      const nativeEmptyState = Number.isFinite(nativeReviewCount) && nativeReviewCount === 0;
      summary.className = nativeEmptyState
        ? 'pdp-reviews__score pdp-reviews__score--empty'
        : 'pdp-reviews__empty-copy pdp-reviews__empty-copy--error';
      summary.innerHTML = nativeEmptyState
        ? renderJudgeReviewSummary(0, 0)
        : '<strong>Reviews are temporarily unavailable</strong><span>Please try again shortly.</span>';
      section.classList.remove('pdp-reviews--loading');
      section.classList.add('pdp-reviews--empty');
      section.setAttribute('aria-busy', 'false');
      judgeReviewMedia = [];
      if (headerMediaPreview) headerMediaPreview.hidden = true;
      if (!$$('.pdp-studio-note', list).length) list.hidden = true;
      const controls = $('[data-review-list-controls]', section);
      if (controls) controls.hidden = true;
      return;
    }
    const knownReviewKeys = new Set(judgeReviewCache.map(judgeReviewKey));
    incomingReviews.forEach((review) => {
      const key = judgeReviewKey(review);
      if (knownReviewKeys.has(key)) {
        if (isCampaignReviewPage) Object.assign(judgeReviewCache.find(item => judgeReviewKey(item) === key), review);
        return;
      }
      judgeReviewCache.push(review);
      knownReviewKeys.add(key);
    });
    const reviews = judgeReviewCache;
    const displayReviews = reviews.slice(0, visibleJudgeReviewCount);
    const aggregateRating = campaignReviewRatings?.aggregate(bridge) || { rating: null, count: null };
    const average = isCampaignReviewPage ? aggregateRating.rating : reviews.reduce((total, review) => total + review.rating, 0) / reviews.length;
    summary.className = 'pdp-reviews__score';
    const reviewCount = (isCampaignReviewPage ? aggregateRating.count : null) ?? (nativeReviewCount || reviews.length);
    summary.innerHTML = isCampaignReviewPage
      ? (campaignReviewRatings?.summary(aggregateRating, reviews.length) || '')
      : renderJudgeReviewSummary(average, reviewCount);
    $$('[data-judge-review-card]', list).forEach((card) => card.remove());
    const studioCards = $$('.pdp-studio-note', list);
    const hasExpanded = visibleJudgeReviewCount > 6;
    studioCards.forEach((card) => {
      card.hidden = true;
      card.setAttribute('aria-hidden', 'true');
    });
    const reviewCards = displayReviews.map(renderJudgeReviewCard).join('');
    list.insertAdjacentHTML('afterbegin', reviewCards);
    $$('[data-judge-review-card]', list).forEach((card) => {
      const review = displayReviews[Number(card.dataset.judgeReviewCard)];
      card._reviewData = review;
      $$('[data-open-review-detail]', card).forEach((button) => { button._reviewData = review; });
    });
    const renderedReviewCards = $$('[data-judge-review-card]', list);
    renderedReviewCards.forEach((card, index) => {
      card.classList.toggle('pdp-review-card--divided', index < renderedReviewCards.length - 1);
    });
    list.hidden = false;

    if (headerMedia) {
      const seenMedia = new Set();
      const headerMediaItems = [];
      reviews.forEach((review, reviewIndex) => {
        review.images.forEach((image, imageIndex) => {
          if (seenMedia.has(image.src)) return;
          seenMedia.add(image.src);
          headerMediaItems.push({ review, reviewIndex, image, imageIndex });
        });
      });
      judgeReviewMedia = headerMediaItems.map((item) => ({ image: item.image, review: item.review }));
      headerMedia.innerHTML = headerMediaItems.map((item, index) => `<button class="pdp-reviews__media-item" type="button" data-open-review-gallery="${index}" aria-label="Open customer photo ${index + 1} of ${headerMediaItems.length} from ${escapeHTML(item.review.name)}"><img src="${escapeHTML(item.image.thumb)}" alt="" loading="eager" decoding="async"></button>`).join('');
      if (headerMediaPreview) {
        headerMediaPreview.hidden = headerMediaItems.length === 0;
        setupReviewMediaPreview(headerMediaPreview);
        window.requestAnimationFrame(() => syncReviewMediaPreview(headerMediaPreview));
      }
    }
    const availableReviewCards = Math.max(reviewCount, reviews.length);
    const visibleReviewCards = displayReviews.length;
    list.dataset.reviewTotal = String(availableReviewCards);

    const controls = $('[data-review-list-controls]', section);
    const loadMore = $('[data-review-load-more]', controls);
    const collapse = $('[data-review-collapse]', controls);
    if (controls && loadMore && collapse) {
      const hasMore = visibleReviewCards < availableReviewCards;
      controls.hidden = availableReviewCards <= 6;
      loadMore.hidden = !hasMore;
      collapse.hidden = !hasExpanded;
      loadMore.disabled = false;
      loadMore.classList.remove('is-loading');
      const remaining = Math.max(0, availableReviewCards - visibleReviewCards);
      loadMore.textContent = remaining > 0 && remaining <= 6
        ? `Load ${remaining} more ${remaining === 1 ? 'review' : 'reviews'}`
        : 'Load 6 more reviews';
    }

    const mediaSection = $('[data-review-media-section]');
    const mediaGrid = $('[data-review-media-grid]');
    if (mediaSection && mediaGrid) {
      const mediaLimit = 4;
      $$('[data-judge-review-media]', mediaGrid).forEach((item) => item.remove());
      const customerMedia = [];
      const reviewsWithMedia = displayReviews
        .map((review, reviewIndex) => ({ review, reviewIndex }))
        .filter((item) => item.review.images.length);
      reviewsWithMedia.forEach((item) => {
        if (customerMedia.length >= mediaLimit) return;
        customerMedia.push({ ...item, image: item.review.images[0], imageIndex: 0 });
      });
      let additionalImageIndex = 1;
      while (customerMedia.length < mediaLimit && reviewsWithMedia.some((item) => item.review.images[additionalImageIndex])) {
        reviewsWithMedia.forEach((item) => {
          if (customerMedia.length >= mediaLimit || !item.review.images[additionalImageIndex]) return;
          customerMedia.push({
            ...item,
            image: item.review.images[additionalImageIndex],
            imageIndex: additionalImageIndex
          });
        });
        additionalImageIndex += 1;
      }
      mediaGrid.insertAdjacentHTML('afterbegin', customerMedia.map((item) => `<button class="pdp-review-media__item" type="button" data-judge-review-media="${item.reviewIndex}" data-open-review-detail="${item.imageIndex}" aria-label="Open customer photo ${item.imageIndex + 1} from ${escapeHTML(item.review.name)}"><img src="${escapeHTML(item.image.thumb)}" alt="" loading="eager" decoding="async"><span>Customer photo</span></button>`).join(''));
      $$('[data-judge-review-media]', mediaGrid).forEach((button) => {
        button._reviewData = displayReviews[Number(button.dataset.judgeReviewMedia)];
      });
      const studioMedia = $$('.pdp-review-media__item:not([data-judge-review-media])', mediaGrid);
      const studioSlots = Math.max(0, mediaLimit - customerMedia.length);
      studioMedia.forEach((item, index) => { item.hidden = index >= studioSlots; });
      mediaSection.hidden = customerMedia.length + Math.min(studioSlots, studioMedia.length) === 0;
    }

    section.classList.remove('pdp-reviews--empty', 'pdp-reviews--loading');
    section.setAttribute('aria-busy', 'false');
    const ratingSlot = $('[data-real-review-rating]');
    if (ratingSlot) {
      ratingSlot.hidden = isCampaignReviewPage && average === null;
      ratingSlot.innerHTML = isCampaignReviewPage
        ? (campaignReviewRatings?.inline(aggregateRating) || '')
        : `<span class="review-rating-inline" aria-label="${average.toFixed(1)} out of 5 stars"><span class="review-rating-inline__stars" aria-hidden="true">★★★★★</span><span>${average.toFixed(1)}</span><span>(${reviewCount})</span></span>`;
    }
  };

  const watchJudgeReviews = () => {
    const bridge = $('[data-judgeme-submission-bridge]');
    if (!bridge) return;
    let syncTimer;
    if (isCampaignReviewPage) {
      const ratingObserver = new MutationObserver(() => {
        window.clearTimeout(syncTimer);
        syncTimer = window.setTimeout(syncJudgeReviews, 80);
      });
      ratingObserver.observe(bridge, { attributes: true, subtree: true, attributeFilter: ['data-score', 'aria-label', 'alt', 'data-average-rating', 'data-number-of-reviews'] });
    }
    const observer = new MutationObserver((mutations) => {
      const widgetChanged = mutations.some((mutation) => bridge.contains(mutation.target));
      if (widgetChanged) {
        window.clearTimeout(syncTimer);
        syncTimer = window.setTimeout(syncJudgeReviews, 80);
      }
      const modal = $('.jdgm-write-review-modal');
      const nativeClose = $('.jdgm-write-review-modal__close-btn', modal);
      if (nativeClose) {
        nativeClose.setAttribute('aria-label', 'Close review form');
        nativeClose.removeAttribute('hidden');
      }
      const mediaInput = $('input[name="media"]', modal);
      const thankYouClose = $('.jdgm-write-review-modal__thank-you-nav .jdgm-write-review-modal__nav-btn-close', modal);
      if (thankYouClose && thankYouClose.textContent.trim() !== 'Done') {
        thankYouClose.textContent = 'Done';
        thankYouClose.setAttribute('aria-label', 'Finish and close review form');
      }
      if (mediaInput) {
        mediaInput.required = false;
        mediaInput.removeAttribute('required');
        mediaInput.accept = 'image/*';
        mediaInput.removeAttribute('aria-required');
        const mediaGroup = mediaInput.closest('.jdgm-write-review-modal__form-group--share-media');
        mediaGroup?.classList.remove('is-required');
        mediaGroup?.classList.add('is-optional');
      }
      const activeThankYouPage = $('.jdgm-write-review-modal__page--thank-you.current-page, .jdgm-write-review-modal__section--page--thank-you.current-page', modal);
      if (modal && activeThankYouPage && modal.dataset.reviewSuccessHandled !== 'true') {
        resetJudgeReviewSubmitting(modal);
        modal.dataset.reviewSuccessHandled = 'true';
        bridge.classList.add('is-submitted');
        announce('Thank you. Your review was submitted for moderation.');
      }
      if (modal?.classList.contains('is-submitting')) {
        const visibleError = $$(
          '.jdgm-write-review-modal__error,[role="alert"],.jdgm-form__error,.jdgm-write-review-modal__field-error',
          modal
        ).some((item) => item.offsetParent !== null && item.textContent.trim());
        if (visibleError) resetJudgeReviewSubmitting(modal);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    window.setTimeout(syncJudgeReviews, 1200);
    window.setTimeout(() => {
      judgeReviewLoadingTimedOut = true;
      syncJudgeReviews();
    }, 5000);
  };

  const setLayerOpen = (shell, open, bodyClass) => {
    if (!shell) return;
    if (open) {
      lastFocus = document.activeElement;
      shell.hidden = false;
      requestAnimationFrame(() => shell.classList.add('is-open'));
      document.body.classList.add(bodyClass);
      syncPageScrollLock();
      window.setTimeout(() => $(focusableSelector, shell)?.focus(), 40);
    } else {
      shell.classList.remove('is-open');
      document.body.classList.remove(bodyClass);
      syncPageScrollLock();
      window.setTimeout(() => { shell.hidden = true; }, 220);
      if (lastFocus instanceof HTMLElement) lastFocus.focus({ preventScroll: true });
      if (!pageLockClasses.some((className) => document.body.classList.contains(className))) {
        restorePageScroll(lockedPageScrollY);
      }
    }
  };

  const trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const layer = [productModal, cartShell, searchShell].find((item) => item && !item.hidden && item.classList.contains('is-open'));
    if (!layer) return;
    const items = $$(focusableSelector, layer).filter((item) => item.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const cartProductCache = new Map();

  const getCartProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!cartProductCache.has(handle)) {
      const request = fetch(`${config.routes.root}products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : null)
        .catch(() => null);
      cartProductCache.set(handle, request);
    }
    return cartProductCache.get(handle);
  };

  const getCartComparePrices = async (items) => Promise.all(items.map(async (item) => {
    const product = await getCartProduct(item.handle);
    const variant = product?.variants?.find((candidate) => Number(candidate.id) === Number(item.variant_id));
    return Number(variant?.compare_at_price || 0);
  }));

  const compositionCardPriceCache = new Map();

  const getCompositionCardPrice = (productUrl) => {
    if (!productUrl) return Promise.reject(new Error('Missing composition product URL.'));
    if (!compositionCardPriceCache.has(productUrl)) {
      const requestUrl = new URL(productUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', 'composition-card-price');
      const currentParams = new URLSearchParams(window.location.search);
      ['preview_theme_id', 'country'].forEach((name) => {
        const value = currentParams.get(name);
        if (value) requestUrl.searchParams.set(name, value);
      });
      const request = fetch(`${requestUrl.pathname}${requestUrl.search}`, {
        headers: { Accept: 'text/html' }
      }).then(async (response) => {
        if (!response.ok) throw new Error('Could not load the composition price.');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const price = $('[data-composition-card-price-fragment] > .card-price--look', html);
        if (!price) throw new Error('Composition price markup is missing.');
        return price.outerHTML;
      });
      compositionCardPriceCache.set(productUrl, request);
    }
    return compositionCardPriceCache.get(productUrl);
  };

  const hydrateCompositionCardPrice = async (target) => {
    try {
      const markup = await getCompositionCardPrice(target.dataset.productUrl);
      if (!target.isConnected) return;
      const html = new DOMParser().parseFromString(markup, 'text/html');
      const price = $('.card-price--look', html);
      if (!price) throw new Error('Composition price markup is invalid.');
      target.className = price.className;
      target.innerHTML = price.innerHTML;
      target.dataset.compositionPriceResolved = price.dataset.compositionPriceResolved || 'false';
      target.removeAttribute('data-composition-card-price');
      target.removeAttribute('data-product-url');
      target.removeAttribute('data-error-label');
      target.removeAttribute('aria-busy');
    } catch (_) {
      if (!target.isConnected) return;
      const status = document.createElement('span');
      status.className = 'card-price--look__status';
      status.textContent = target.dataset.errorLabel || '';
      target.classList.remove('card-price--look-loading');
      target.classList.add('card-price--look-error');
      target.replaceChildren(status);
      target.removeAttribute('data-composition-card-price');
      target.removeAttribute('aria-busy');
    }
  };

  const loadCompositionCardPrices = () => {
    const targets = $$('[data-composition-card-price]');
    if (!targets.length) return;
    let nextTarget = 0;
    const worker = async () => {
      while (nextTarget < targets.length) {
        const target = targets[nextTarget];
        nextTarget += 1;
        await hydrateCompositionCardPrice(target);
      }
    };
    void Promise.all(Array.from({ length: Math.min(4, targets.length) }, worker));
  };

  const cartMarkup = async (cart) => {
    if (!cart.item_count) return '<div class="empty-state"><p>Your cart is currently empty.</p><a class="btn btn--primary" href="/collections/all">Shop now</a></div>';
    const comparePrices = await getCartComparePrices(cart.items);
    let cartReferenceTotal = 0;
    const lines = cart.items.map((item, index) => {
      const imageUrl = item.image ? `${item.image}${item.image.includes('?') ? '&' : '?'}width=220` : '';
      const image = imageUrl ? `<img src="${escapeHTML(imageUrl)}" alt="${escapeHTML(item.product_title)}" loading="lazy">` : '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cart-line__meta">${escapeHTML(item.variant_title)}</p>` : '';
      const finalLinePrice = Number(item.final_line_price || 0);
      const compareLinePrice = comparePrices[index] * Math.max(1, Number(item.quantity || 1));
      const referenceLinePrice = Math.max(Number(item.original_line_price || 0), compareLinePrice);
      const compareMarkup = referenceLinePrice > finalLinePrice
        ? `<s class="cart-price__compare">${money(referenceLinePrice)}</s>`
        : '';
      cartReferenceTotal += referenceLinePrice;
      return `<article class="cart-line">
        <a class="cart-line__image" href="${escapeHTML(item.url)}">${image}</a>
        <div><h3><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a></h3>${variant}
          <div class="cart-line__actions">
            <div class="quantity-control"><button type="button" data-cart-change data-line="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><input value="${item.quantity}" readonly aria-label="Quantity"><button type="button" data-cart-change data-line="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div>
            <button class="cart-remove-icon" type="button" data-cart-change data-line="${index + 1}" data-quantity="0" aria-label="Remove ${escapeHTML(item.product_title)} from cart"${document.body.classList.contains('campaign-lab-page') ? '' : ' title="Remove from cart"'} data-tooltip="Remove item"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-8 0 1 13h8l1-13M10 11v5m4-5v5"/></svg></button>
          </div>
        </div>
        <div class="cart-line__price"><strong>${money(finalLinePrice)}</strong>${compareMarkup}</div>
      </article>`;
    }).join('');
    const totalCompareMarkup = cartReferenceTotal > Number(cart.total_price || 0)
      ? `<s class="cart-price__compare">${money(cartReferenceTotal)}</s>`
      : '';
    return `<div class="drawer-cart-lines">${lines}</div><div class="drawer-summary"><div class="summary-row summary-row--total"><strong>Subtotal</strong><span class="summary-row__prices"><strong>${money(cart.total_price)}</strong>${totalCompareMarkup}</span></div><form action="${escapeHTML(config.routes.cart)}" method="post"><button class="btn btn--primary btn--wide" type="submit" name="checkout">Secure checkout</button></form><div data-cart-accelerated-slot></div></div>`;
  };

  const renderCart = async (cart) => {
    const target = $('[data-cart-drawer-content]');
    $$('[data-cart-count]').forEach((element) => { element.textContent = cart.item_count; });
    $$('[data-open-cart]').forEach((element) => element.setAttribute('aria-label', `Shopping cart, ${cart.item_count} items`));
    if (target) {
      target.innerHTML = await cartMarkup(cart);
      const acceleratedTemplate = $('[data-cart-accelerated-template]');
      const acceleratedSlot = $('[data-cart-accelerated-slot]', target);
      if (acceleratedTemplate?.content && acceleratedSlot) {
        acceleratedSlot.replaceWith(acceleratedTemplate.content.cloneNode(true));
        window.Shopify?.PaymentButton?.init?.();
      }
    }
  };

  const getCart = async () => {
    const response = await fetch(`${config.routes.cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Could not load your cart.');
    return response.json();
  };

  const changeCart = async (line, quantity) => {
    const response = await fetch(`${config.routes.cartChange}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Could not update your cart.');
    const cart = await response.json();
    await renderCart(cart);
    return cart;
  };

  const addItems = async (items) => {
    const response = await fetch(`${config.routes.cartAdd}.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || 'One or more items are unavailable.');
    }
    const cart = await getCart();
    await renderCart(cart);
    setLayerOpen(cartShell, true, 'drawer-open');
    return cart;
  };

  const closeMega = () => {
    $$('[data-mega-menu]').forEach((menu) => { menu.classList.remove('is-open'); menu.setAttribute('aria-hidden', 'true'); });
    $$('[data-mega-trigger]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    document.body.classList.remove('mega-menu-open');
  };

  const openMega = (trigger) => {
    if (window.innerWidth < 990) return;
    const menu = $(`[data-mega-menu="${CSS.escape(trigger.dataset.megaTrigger)}"]`);
    if (!menu || !$('.mega-menu__heading, .mega-menu__feature:not(.mega-menu__feature--empty)', menu)) return;
    const shouldOpen = !menu.classList.contains('is-open');
    closeMega();
    if (shouldOpen) {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mega-menu-open');
    }
  };

  const toggleMobileMenu = () => {
    const header = $('[data-site-header]');
    const button = $('[data-menu-toggle]');
    const menu = $('[data-mobile-nav]');
    if (!header || !button || !menu) return;
    const open = !header.classList.contains('site-header--menu-open');
    header.classList.toggle('site-header--menu-open', open);
    document.body.classList.toggle('menu-is-open', open);
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    menu.setAttribute('aria-hidden', String(!open));
    menu.inert = !open;
    if (!open) {
      $$('[data-mobile-submenu-toggle][aria-expanded="true"]', menu).forEach((toggle) => {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', toggle.getAttribute('aria-label')?.replace(/^Hide /, 'Show ') || 'Show categories');
        const panel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (panel) panel.hidden = true;
      });
    }
  };

  const updateProductQuantityPricing = (section) => {
    if (!section) return;
    const quantityField = $('[data-product-quantity]', section);
    const linePrice = $('[data-line-price]', section);
    if (!quantityField || !linePrice) return;
    const minimum = Math.max(1, Number(quantityField.min || 1));
    const maximum = quantityField.max ? Number(quantityField.max) : Number.POSITIVE_INFINITY;
    const quantity = Math.min(maximum, Math.max(minimum, Number(quantityField.value || minimum)));
    const unitPrice = Number(linePrice.dataset.unitPrice || 0);
    const unitComparePrice = Math.max(unitPrice, Number($('[data-line-compare-price]', section)?.dataset.unitComparePrice || 0));
    const selectedVase = $('[data-vase-choice]:checked:not(:disabled)', section);
    const vaseQuantityField = selectedVase?.value ? $('[data-vase-quantity]', selectedVase.closest('[data-vase-card]')) : null;
    const vaseQuantity = vaseQuantityField ? Math.max(1, Number(vaseQuantityField.value || 1)) : 0;
    const vaseUnitPrice = selectedVase?.value ? Math.max(0, Number(selectedVase.dataset.vaseUnitPrice || 0)) : 0;
    const totalPrice = (unitPrice * quantity) + (vaseUnitPrice * vaseQuantity);
    quantityField.value = String(quantity);
    linePrice.textContent = money(unitPrice);
    const comparePrice = $('[data-line-compare-price]', section);
    if (comparePrice) {
      comparePrice.textContent = money(unitComparePrice);
      comparePrice.hidden = !(unitComparePrice > unitPrice);
    }
    const discountLabel = $('[data-set-discount-label],[data-discount-label]', section);
    if (discountLabel) {
      const discountPercent = unitComparePrice > unitPrice
        ? Math.round(((unitComparePrice - unitPrice) * 100) / unitComparePrice)
        : 0;
      discountLabel.textContent = discountPercent > 0 ? `${discountPercent}% OFF` : '';
      discountLabel.hidden = discountPercent === 0;
    }
    const addPrice = $('[data-add-button-price]', section);
    const mobilePrice = $('[data-mobile-price]', section);
    const campaignBuybarPrice = $('[data-campaign-buybar-price]', section);
    if (addPrice && !$('[data-add-button]', section)?.disabled) addPrice.textContent = ` · ${money(totalPrice)}`;
    if (mobilePrice) mobilePrice.textContent = money(totalPrice);
    if (campaignBuybarPrice) campaignBuybarPrice.textContent = money(totalPrice);
    $$('[data-quantity-display]', section).forEach((item) => { item.textContent = String(quantity); });
    const pieceContext = $('[data-set-piece-context]', section);
    if (pieceContext) {
      const pieceCount = Math.max(0, Number(pieceContext.dataset.basePieceCount || 0));
      const vaseCopy = vaseQuantity ? ` plus ${vaseQuantity} ${vaseQuantity === 1 ? 'vase' : 'vases'}` : '';
      pieceContext.textContent = `${pieceCount} ${pieceCount === 1 ? 'piece' : 'pieces'} per composition${vaseCopy}. The total updates when you change an option or quantity.`;
    }
  };

  const getSelectedVase = (section) => {
    const choice = $('[data-vase-choice]:checked:not(:disabled)', section);
    if (!choice?.value) return null;
    const quantityField = $('[data-vase-quantity]', choice.closest('[data-vase-card]'));
    const minimum = Math.max(1, Number(quantityField?.min || 1));
    const maximum = quantityField?.max ? Number(quantityField.max) : Number.POSITIVE_INFINITY;
    const quantity = Math.min(maximum, Math.max(minimum, Number(quantityField?.value || minimum)));
    if (quantityField) quantityField.value = String(quantity);
    return { id: Number(choice.value), quantity };
  };

  const syncVaseSelector = (section) => {
    if (!section) return;
    const selectedChoice = $('[data-vase-choice]:checked:not(:disabled)', section);
    $$('[data-vase-card]', section).forEach((card) => {
      const choice = $('[data-vase-choice]', card);
      if (choice?.disabled) choice.checked = false;
      const isSelected = choice === selectedChoice && !choice?.disabled;
      card.classList.toggle('is-selected', isSelected);
      if (choice) choice.setAttribute('aria-checked', String(isSelected));
      $$('[data-vase-quantity],[data-vase-quantity-minus],[data-vase-quantity-plus]', card).forEach((control) => {
        control.disabled = !isSelected;
      });
    });
    const selectedVase = getSelectedVase(section);
    const hasVase = Boolean(selectedVase);
    section.dataset.setVaseIncluded = String(hasVase);
    const acceleratedVaseId = $('[data-accelerated-vase-id]', section);
    const acceleratedVaseQuantity = $('[data-accelerated-vase-quantity]', section);
    if (acceleratedVaseId) {
      acceleratedVaseId.value = selectedVase ? String(selectedVase.id) : '';
      acceleratedVaseId.disabled = !selectedVase;
    }
    if (acceleratedVaseQuantity) {
      acceleratedVaseQuantity.value = selectedVase ? String(selectedVase.quantity) : '1';
      acceleratedVaseQuantity.disabled = !selectedVase;
    }
    updateProductQuantityPricing(section);
  };

  const addBusinessDays = (sourceDate, days) => {
    const result = new Date(sourceDate.getFullYear(), sourceDate.getMonth(), sourceDate.getDate(), 12);
    let remaining = Math.max(0, Number(days || 0));
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) remaining -= 1;
    }
    return result;
  };

  const formatDeliveryDate = (date) => new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
    month: 'short', day: 'numeric'
  }).format(date);

  const initDeliveryEstimate = (section) => {
    const estimate = $('[data-delivery-estimate]', section);
    const dataNode = $('[data-delivery-ranges]', estimate);
    if (!estimate || !dataNode) return;
    let deliveryData = {};
    try { deliveryData = JSON.parse(dataNode.textContent || '{}'); } catch (_) { return; }
    const today = new Date();
    const processingDays = Math.max(0, Number(deliveryData.processingBusinessDays ?? 2));
    const processingDate = addBusinessDays(today, processingDays);
    const countryRange = deliveryData.destinations?.[String(estimate.dataset.country || '').toUpperCase()];
    const ordered = $('[data-delivery-ordered]', estimate);
    const processing = $('[data-delivery-processing]', estimate);
    const arrival = $('[data-delivery-arrival]', estimate);
    const note = $('[data-delivery-note]', estimate);
    if (ordered) ordered.textContent = formatDeliveryDate(today);
    if (processing) processing.textContent = formatDeliveryDate(processingDate);
    if (!arrival) return;
    if (!countryRange) {
      arrival.textContent = estimate.dataset.checkoutLabel || 'Confirmed at checkout';
      if (note) note.textContent = estimate.dataset.unsupportedNote || note.textContent;
      return;
    }
    const earliest = addBusinessDays(processingDate, Number(countryRange.min || 0));
    const latest = addBusinessDays(processingDate, Number(countryRange.max || countryRange.min || 0));
    arrival.textContent = `${formatDeliveryDate(earliest)} – ${formatDeliveryDate(latest)}`;
  };

  const syncProductGalleryLayout = (gallery) => {
    if (!gallery) return;
    Array.from(gallery.classList).forEach((className) => {
      if (/^pdp-gallery--count-\d+$/.test(className)) gallery.classList.remove(className);
    });
    const count = gallery.children.length;
    gallery.classList.add(`pdp-gallery--count-${count}`);
    gallery.classList.toggle('pdp-gallery--odd', count > 1 && count % 2 === 1);
  };

  /* Product storytelling should never leave a blank beige card behind when a
     CDN image fails. Keep a lone primary product image, but remove failed
     supporting/review media and immediately recompute the gallery layout. */
  document.addEventListener('error', (event) => {
    const image = event.target;
    if (!(image instanceof HTMLImageElement)) return;
    const reviewMedia = image.closest('.pdp-review-media__item');
    if (reviewMedia) {
      const mediaSection = reviewMedia.closest('[data-review-media-section]');
      reviewMedia.remove();
      if (mediaSection && !mediaSection.querySelector('.pdp-review-media__item:not([hidden])')) {
        mediaSection.hidden = true;
      }
      return;
    }
    const galleryItem = image.closest('[data-product-gallery] .pdp-gallery__item');
    const gallery = galleryItem?.closest('[data-product-gallery]');
    if (!galleryItem || !gallery || gallery.children.length <= 1) return;
    galleryItem.remove();
    syncProductGalleryLayout(gallery);
  }, true);

  const renderVariantGallery = (section, variant, variants) => {
    const gallery = $('[data-product-gallery]', section);
    const galleryData = $('[data-variant-galleries]', section);
    if (!gallery || !galleryData) return false;
    let galleries = {};
    try { galleries = JSON.parse(galleryData.textContent || '{}'); } catch (_) {}
    let galleryKey = String(variant.id);
    let images = galleries[galleryKey];
    if (!Array.isArray(images) || !images.length) {
      const optionGroups = $$('[data-product-option]', section);
      const colourIndex = optionGroups.findIndex((group) => ['color', 'colour'].includes(group.dataset.optionName));
      if (colourIndex >= 0) {
        const matchingVariant = variants.find((candidate) => (
          candidate.options[colourIndex] === variant.options[colourIndex]
          && Array.isArray(galleries[String(candidate.id)])
          && galleries[String(candidate.id)].length
        ));
        if (matchingVariant) {
          galleryKey = String(matchingVariant.id);
          images = galleries[galleryKey];
        }
      }
    }

    if (Array.isArray(images) && images.length) {
      if (gallery.dataset.galleryMode !== 'variant' || gallery.dataset.galleryVariant !== galleryKey) {
        const variantTitle = variant.public_title || variant.title || '';
        gallery.innerHTML = images.map((image, index) => {
          const alt = image.alt || variantTitle || 'Product image';
          const srcset = [
            image.src480 && `${image.src480} 480w`,
            image.src720 && `${image.src720} 720w`,
            image.src960 && `${image.src960} 960w`,
            image.src1200 && `${image.src1200} 1200w`,
            image.src1500 && `${image.src1500} 1500w`,
            image.src && `${image.src} 1800w`
          ].filter(Boolean).join(', ');
          return `<div class="pdp-gallery__item product-gallery__item pdp-gallery__item--${index === 0 ? 'product' : 'context'}" data-variant-gallery-item>
            <button class="pdp-gallery__preview product-gallery__zoom" type="button" data-product-zoom="${escapeHTML(image.zoom || image.src)}" aria-label="Open image ${index + 1} of ${images.length} for ${escapeHTML(variantTitle)}">
              <img src="${escapeHTML(image.src)}"${srcset ? ` srcset="${escapeHTML(srcset)}"` : ''} sizes="(min-width: 1024px) 36vw, 86vw" alt="${escapeHTML(alt)}" ${index === 0 ? 'loading="eager" fetchpriority="high"' : index === 1 ? 'loading="eager"' : 'loading="lazy"'}>
            </button>
          </div>`;
        }).join('');
        gallery.dataset.galleryMode = 'variant';
        gallery.dataset.galleryVariant = galleryKey;
      }
      syncProductGalleryLayout(gallery);
      if (window.innerWidth < 990) gallery.scrollTo({ left: 0, behavior: 'smooth' });
      return true;
    }

    if (gallery.dataset.galleryMode === 'variant') {
      const fallback = $('[data-product-gallery-fallback]', section);
      if (fallback) gallery.replaceChildren(fallback.content.cloneNode(true));
    }
    gallery.dataset.galleryMode = 'product';
    gallery.dataset.galleryVariant = String(variant.id);
    syncProductGalleryLayout(gallery);
    return false;
  };

  const openProductGallery = (trigger) => {
    const section = trigger.closest('[data-product-section]');
    const gallery = trigger.closest('[data-product-gallery],[data-review-gallery]');
    const content = $('[data-product-modal-content]');
    if (!gallery || !content) return;
    const buttons = $$('[data-product-zoom]', gallery);
    const images = buttons.map((button) => {
      const image = $('img', button);
      return {
        src: button.dataset.productZoom || image?.currentSrc || image?.src || '',
        thumb: image?.currentSrc || image?.src || button.dataset.productZoom || '',
        alt: image?.alt || section?.querySelector('.pdp-title')?.textContent?.trim() || 'Product image'
      };
    }).filter((image) => image.src);
    if (!images.length) return;
    const activeIndex = Math.max(0, buttons.indexOf(trigger));
    const active = images[activeIndex] || images[0];
    content.classList.add('modal-card--gallery');
    content.innerHTML = `
      <button class="close-button quick-view__close" type="button" data-close-product-modal aria-label="Close gallery">×</button>
      <div class="product-gallery-viewer" data-product-gallery-viewer>
        <div class="product-gallery-viewer__main">
          <img src="${escapeHTML(active.src)}" alt="${escapeHTML(active.alt)}">
          <span data-product-gallery-count>${activeIndex + 1} / ${images.length}</span>
        </div>
        <div class="product-gallery-viewer__rail" aria-label="All product images">
          ${images.map((image, index) => `<button class="product-gallery-viewer__thumb${index === activeIndex ? ' is-selected' : ''}" type="button" data-product-gallery-select="${index}" data-gallery-src="${escapeHTML(image.src)}" data-gallery-alt="${escapeHTML(image.alt)}" aria-label="Show image ${index + 1} of ${images.length}" aria-pressed="${index === activeIndex ? 'true' : 'false'}"><img src="${escapeHTML(image.thumb)}" alt="" loading="lazy"></button>`).join('')}
        </div>
      </div>`;
    setLayerOpen(productModal, true, 'modal-open');
  };

  const renderReviewDetailCopy = (review) => `<div class="pdp-review-card__meta">${isCampaignReviewPage ? (campaignReviewRatings?.stars(review.rating) || '') : `<span class="pdp-reviews__stars" aria-label="${review.rating} out of 5 stars">★★★★★</span>`}${review.verified ? '<span class="pdp-review-card__verified">Verified purchase</span>' : ''}</div>
    ${review.title ? `<h2>${escapeHTML(review.title)}</h2>` : ''}
    <p>${escapeHTML(review.body)}</p>
    <footer><strong>${escapeHTML(review.name)}</strong>${review.date ? `<time>${escapeHTML(review.date)}</time>` : ''}</footer>`;

  const syncReviewGalleryCopy = (viewer, index) => {
    const item = viewer?._reviewGalleryItems?.[index];
    const copy = viewer?.closest('.review-detail-viewer')?.querySelector('.review-detail-viewer__copy');
    if (item?.review && copy) copy.innerHTML = renderReviewDetailCopy(item.review);
  };

  const setupReviewGalleryWheel = (viewer) => {
    const main = $('.product-gallery-viewer__main', viewer);
    if (!main || $$('[data-product-gallery-select]', viewer).length < 2) return;
    let lastWheelAt = 0;
    let wheelDelta = 0;
    let steppedThisGesture = false;
    main.addEventListener('wheel', (event) => {
      if (event.ctrlKey || event.metaKey) return;
      const dominantDelta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (!dominantDelta) return;
      event.preventDefault();
      const now = performance.now();
      if (now - lastWheelAt > 180) {
        wheelDelta = 0;
        steppedThisGesture = false;
      }
      lastWheelAt = now;
      if (steppedThisGesture) return;
      wheelDelta += dominantDelta * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? main.clientHeight : 1);
      if (Math.abs(wheelDelta) < 30) return;
      const stepButton = $(`[data-gallery-step="${wheelDelta > 0 ? 1 : -1}"]`, viewer);
      if (stepButton) stepProductGallery(stepButton);
      wheelDelta = 0;
      steppedThisGesture = true;
    }, { passive: false });
  };

  const openReviewDetail = (trigger) => {
    const isGlobalGallery = trigger.matches('[data-open-review-gallery]');
    const review = trigger._reviewData || trigger.closest('[data-judge-review-card]')?._reviewData;
    const content = $('[data-product-modal-content]');
    const items = isGlobalGallery
      ? judgeReviewMedia
      : (review?.images || []).map((image) => ({ image, review }));
    if (!content || !items.length) return;
    const requestedIndex = Number(isGlobalGallery ? trigger.dataset.openReviewGallery : trigger.dataset.openReviewDetail || 0);
    const activeIndex = Math.max(0, Math.min(items.length - 1, requestedIndex));
    const active = items[activeIndex];
    content.classList.add('modal-card--gallery', 'modal-card--review');
    content.innerHTML = `
      <button class="close-button quick-view__close" type="button" data-close-product-modal aria-label="Close review">×</button>
      <div class="review-detail-viewer">
        <div class="product-gallery-viewer review-detail-viewer__gallery" data-product-gallery-viewer>
          <div class="product-gallery-viewer__main">
            <img src="${escapeHTML(active.image.src)}" alt="Customer review photo">
            ${items.length > 1 ? '<button class="review-detail-viewer__arrow review-detail-viewer__arrow--previous" type="button" data-gallery-step="-1" aria-label="Previous photo">‹</button><button class="review-detail-viewer__arrow review-detail-viewer__arrow--next" type="button" data-gallery-step="1" aria-label="Next photo">›</button>' : ''}
            <span data-product-gallery-count>${activeIndex + 1} / ${items.length}</span>
          </div>
          <div class="product-gallery-viewer__rail" aria-label="All customer photos">
            ${items.map((item, index) => `<button class="product-gallery-viewer__thumb${index === activeIndex ? ' is-selected' : ''}" type="button" data-product-gallery-select="${index}" data-gallery-src="${escapeHTML(item.image.src)}" data-gallery-alt="Customer review photo ${index + 1}" aria-label="Show customer photo ${index + 1} of ${items.length}" aria-pressed="${index === activeIndex ? 'true' : 'false'}"><img src="${escapeHTML(item.image.thumb)}" alt="" loading="lazy"></button>`).join('')}
          </div>
        </div>
        <aside class="review-detail-viewer__copy">${renderReviewDetailCopy(active.review)}</aside>
      </div>`;
    const viewer = $('[data-product-gallery-viewer]', content);
    if (viewer) {
      viewer._reviewGalleryItems = items;
      setupReviewGalleryWheel(viewer);
    }
    setLayerOpen(productModal, true, 'modal-open');
  };

  const selectProductGalleryImage = (target) => {
    const viewer = target.closest('[data-product-gallery-viewer]');
    const mainImage = $('.product-gallery-viewer__main img', viewer);
    const countLabel = $('[data-product-gallery-count]', viewer);
    const thumbnails = $$('[data-product-gallery-select]', viewer);
    const index = Number(target.dataset.productGallerySelect || 0);
    if (mainImage) {
      mainImage.removeAttribute('srcset');
      mainImage.removeAttribute('sizes');
      mainImage.src = target.dataset.gallerySrc;
      mainImage.alt = target.dataset.galleryAlt || '';
    }
    if (countLabel) countLabel.textContent = `${index + 1} / ${thumbnails.length}`;
    thumbnails.forEach((button) => {
      const selected = button === target;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    syncReviewGalleryCopy(viewer, index);
  };

  const stepProductGallery = (target) => {
    const viewer = target.closest('[data-product-gallery-viewer]');
    const thumbnails = $$('[data-product-gallery-select]', viewer);
    const selectedIndex = thumbnails.findIndex((item) => item.classList.contains('is-selected'));
    if (thumbnails.length < 2 || selectedIndex < 0) return;
    const step = Number(target.dataset.galleryStep || 1);
    thumbnails[(selectedIndex + step + thumbnails.length) % thumbnails.length].click();
  };

  const openVaseDetails = (trigger) => {
    const template = document.getElementById(trigger.dataset.openVaseDetails);
    const content = $('[data-product-modal-content]');
    if (!template || !content) return;
    content.className = 'modal-card modal-card--vase-detail';
    content.replaceChildren(template.content.cloneNode(true));
    setLayerOpen(productModal, true, 'modal-open');
  };

  const selectVaseFromModal = (trigger) => {
    const choice = document.getElementById(trigger.dataset.selectVaseFromModal);
    const section = choice?.closest('[data-product-section]');
    if (!choice || choice.disabled || !section) return;
    choice.checked = true;
    syncVaseSelector(section);
    setLayerOpen(productModal, false, 'modal-open');
    window.setTimeout(() => $('[data-product-modal-content]')?.classList.remove('modal-card--vase-detail'), 220);
  };

  const openQuickOptions = (trigger) => {
    const template = document.getElementById(trigger.dataset.openQuickOptions);
    const content = $('[data-product-modal-content]');
    if (!template || !content) return;
    content.classList.remove('modal-card--gallery', 'modal-card--review');
    content.replaceChildren(template.content.cloneNode(true));
    const section = $('[data-product-section]', content);
    if (section) updateVariant(section);
    syncWishlistButtons();
    setLayerOpen(productModal, true, 'modal-open');
  };

  const updateVariant = (section) => {
    const variantsElement = $('[data-product-variants]', section);
    if (!variantsElement) return;
    const variants = JSON.parse(variantsElement.textContent || '[]');
    const selected = $$('[data-product-option]', section).map((group) => $('input:checked', group)?.value);
    const variant = variants.find((item) => item.options.every((option, index) => option === selected[index])) || variants[0];
    if (!variant) return;
    const idField = $('[data-variant-id]', section);
    if (idField) idField.value = variant.id;
    const bundleComponents = $('[data-bundle-parent-variant]', section);
    if (bundleComponents) bundleComponents.hidden = bundleComponents.dataset.bundleParentVariant !== String(variant.id);
    const currentPrice = $('[data-current-price]', section);
    const comparePrice = $('[data-compare-price]', section);
    const discountLabel = $('[data-discount-label]', section);
    const sku = $('[data-product-sku]', section);
    const stock = $('[data-product-stock]', section);
    const addButton = $('[data-add-button]', section);
    const addText = $('[data-add-button-text]', section);
    const addPrice = $('[data-add-button-price]', section);
    const mobilePrice = $('[data-mobile-price]', section);
    const campaignBuybarPrice = $('[data-campaign-buybar-price]', section);
    const mobileButton = $('[data-submit-product-form]', section);
    const mobileButtonText = $('[data-mobile-add-text]', section);
    const unitPriceCopy = $('[data-unit-price-copy]', section);
    const quantityField = $('[data-product-quantity]', section);
    const featuredMediaId = variant.featured_media?.id || variant.featured_image?.id;
    if (currentPrice) {
      currentPrice.dataset.unitPrice = String(variant.price);
      currentPrice.textContent = money(variant.price);
    }
    if (unitPriceCopy) unitPriceCopy.textContent = money(variant.price);
    if (quantityField) {
      if (variant.inventory_management && variant.inventory_quantity > 0) quantityField.max = String(variant.inventory_quantity);
      else quantityField.removeAttribute('max');
    }
    if (mobilePrice) mobilePrice.textContent = money(variant.price);
    if (campaignBuybarPrice) campaignBuybarPrice.textContent = money(variant.price);
    if (comparePrice) { comparePrice.dataset.unitComparePrice = String(variant.compare_at_price || 0); comparePrice.textContent = money(variant.compare_at_price); comparePrice.hidden = !(variant.compare_at_price > variant.price); }
    if (discountLabel) {
      const discountPercent = variant.compare_at_price > variant.price
        ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
        : 0;
      discountLabel.textContent = discountPercent > 0 ? `${discountPercent}% OFF` : '';
      discountLabel.hidden = discountPercent === 0;
    }
    if (sku) sku.textContent = variant.sku ? `SKU ${variant.sku}` : '';
    if (stock) stock.textContent = variant.available ? 'In stock. Delivery details at checkout' : 'Sold out';
    [addButton, mobileButton].forEach((button) => { if (button) button.disabled = !variant.available; });
    if (addText) addText.textContent = variant.available ? 'Add to cart' : 'Sold out';
    if (mobileButtonText) mobileButtonText.textContent = variant.available ? 'Add to cart' : 'Sold out';
    if (addPrice) addPrice.textContent = variant.available ? ` · ${money(variant.price)}` : '';
    $$('[data-product-option]', section).forEach((group) => { const output = $('[data-selected-option]', group); if (output) output.textContent = $('input:checked', group)?.value || ''; });
    const hasVariantGallery = renderVariantGallery(section, variant, variants);
    if (!hasVariantGallery && featuredMediaId) {
      const gallery = $('.pdp-gallery', section);
      const mediaItem = gallery
        ? $$('[data-media-id]', gallery).find((item) => String(item.dataset.mediaId) === String(featuredMediaId))
        : null;
      if (gallery && mediaItem) {
        const previousPrimary = $('.pdp-gallery__item--product', gallery);
        if (previousPrimary && previousPrimary !== mediaItem) {
          previousPrimary.classList.remove('pdp-gallery__item--product');
          previousPrimary.classList.add('pdp-gallery__item--context');
        }
        mediaItem.classList.remove('pdp-gallery__item--context');
        mediaItem.classList.add('pdp-gallery__item--product');
        if (gallery.firstElementChild !== mediaItem) gallery.insertBefore(mediaItem, gallery.firstElementChild);
        if (window.innerWidth < 990) gallery.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }
    const quickImage = $('[data-quick-image], .quick-view__image img', section);
    const quickImagesElement = $('[data-quick-variant-images]', section);
    let quickImages = {};
    try { quickImages = JSON.parse(quickImagesElement?.textContent || '{}'); } catch (_) {}
    const variantImage = quickImages[String(variant.id)] || variant.featured_image?.src || variant.featured_image;
    if (quickImage && variantImage) {
      quickImage.src = variantImage;
      quickImage.removeAttribute('srcset');
      quickImage.alt = variant.featured_image?.alt || section.querySelector('h2')?.textContent?.trim() || quickImage.alt;
    }
    const studioEntry = $('[data-studio-entry]', section);
    if (studioEntry) {
      const studioUrl = new URL(studioEntry.href, document.baseURI);
      studioUrl.searchParams.set('variant', variant.id);
      studioEntry.href = studioUrl.href;
      studioEntry.dataset.studioVariant = String(variant.id);
    }
    updateProductQuantityPricing(section);
    if (!section.hasAttribute('data-quick-options')) {
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
    }
  };

  const wishlistKey = 'floree.wishlist.v1';
  const getWishlistState = () => {
    try {
      const value = JSON.parse(localStorage.getItem(wishlistKey) || '{"products":[],"looks":[]}');
      return value && Array.isArray(value.products) && Array.isArray(value.looks) ? value : { products: [], looks: [] };
    } catch (_) { return { products: [], looks: [] }; }
  };
  const getWishlist = () => getWishlistState().products.map((item) => item.handle).filter(Boolean);
  const setWishlist = (handles) => {
    try {
      const state = getWishlistState();
      state.products = handles.map((handle) => state.products.find((item) => item.handle === handle) || { handle, saved_at: Date.now() });
      localStorage.setItem(wishlistKey, JSON.stringify(state));
    } catch (_) {}
    syncWishlistButtons();
  };
  const editableStudioSetId = (product) => {
    const tags = Array.isArray(product?.tags) ? product.tags : String(product?.tags || '').split(',');
    if (tags.some((tag) => String(tag).trim().toLowerCase() === 'finished-set')) return '';
    if (!tags.some((tag) => String(tag).trim().toLowerCase() === 'editable-set')) return '';
    const mapped = {
      'quiet-magnolia-editable-set': 'quiet-magnolia',
      'soft-morning-editable-set': 'soft-morning-live',
      'gallery-magnolia-editable-set': 'gallery-magnolia-live',
      'console-statement-editable-set': 'console-statement-live'
    };
    return mapped[product.handle] || String(product.handle || '').replace(/-editable-set$/, '');
  };
  const syncWishlistButtons = () => {
    const handles = getWishlist();
    $$('[data-native-wishlist-count]').forEach((item) => { item.textContent = handles.length ? String(handles.length) : ''; });
    $$('.icon-link--wishlist').forEach((link) => {
      link.classList.toggle('has-items', handles.length > 0);
      link.setAttribute('aria-label', `Wishlist, ${handles.length} saved ${handles.length === 1 ? 'item' : 'items'}`);
    });
    $$('[data-wishlist-toggle]').forEach((button) => {
      const saved = handles.includes(button.dataset.wishlistToggle);
      button.classList.toggle('is-saved', saved); button.setAttribute('aria-pressed', String(saved));
      const title = button.dataset.wishlistTitle ? ` ${button.dataset.wishlistTitle}` : '';
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'}${title} ${saved ? 'from' : 'to'} wishlist`);
      const label = $('[data-wishlist-label]', button);
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
    });
  };

  const renderWishlistPage = async () => {
    const grid = $('[data-native-wishlist-grid]');
    if (!grid) return;
    const handles = getWishlist();
    if (!handles.length) { grid.outerHTML = $('[data-wishlist-empty]')?.innerHTML || '<p>Your wishlist is empty.</p>'; return; }
    const products = (await Promise.all(handles.map(async (handle) => {
      const response = await fetch(`${config.routes.root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } });
      return response.ok ? response.json() : null;
    }))).filter(Boolean);
    if (!products.length) { grid.innerHTML = '<p>Your saved products are no longer available.</p>'; return; }
    grid.innerHTML = products.map((product) => {
      const editableSetId = editableStudioSetId(product);
      const cardVariant = product.variants.find((variant) => variant.available) || product.variants[0] || {};
      const cardPrice = Number(cardVariant.price || product.price || 0);
      const cardComparePrice = Number(cardVariant.compare_at_price || 0);
      const cardDiscount = cardComparePrice > cardPrice
        ? Math.round(((cardComparePrice - cardPrice) * 100) / cardComparePrice)
        : 0;
      const cardPriceMarkup = `<div class="card-price"><span class="card-price__amounts"><span class="price-current">${money(cardPrice)}</span>${cardDiscount ? `<s>${money(cardComparePrice)}</s><span class="price-discount">${cardDiscount}% OFF</span>` : ''}</span></div>`;
      const action = editableSetId
        ? `<a class="quick-add" href="${escapeHTML(product.url)}">Choose pieces</a>`
        : `<button class="quick-add" type="button" data-native-quick-add="${cardVariant.id || ''}">Add to cart</button>`;
      return `<article class="product-card${editableSetId ? ' product-card--editable-set' : ''}" data-product-handle="${escapeHTML(product.handle)}"><div class="product-card__media"><a class="product-card__image" href="${escapeHTML(product.url)}"><img src="${escapeHTML(product.featured_image || '')}" alt="${escapeHTML(product.title)}" loading="lazy"></a><button class="wishlist-button is-saved" type="button" data-wishlist-toggle="${escapeHTML(product.handle)}" data-wishlist-title="${escapeHTML(product.title)}" aria-label="Remove ${escapeHTML(product.title)} from wishlist" aria-pressed="true"></button></div><div class="product-card__copy"><p class="micro-label">${editableSetId ? 'Editable composition' : escapeHTML(product.type || product.vendor)}</p><h3><a href="${escapeHTML(product.url)}">${escapeHTML(product.title)}</a></h3><div class="product-card__pricing-row">${editableSetId ? '<div class="card-price card-price--editable-set">Live component pricing</div>' : cardPriceMarkup}</div>${action}</div></article>`;
    }).join('');
    syncWishlistButtons();
  };

  const loadRecommendations = () => {
    $$('[data-product-recommendations]').forEach((section) => {
      if (!section.dataset.url) return;
      fetch(section.dataset.url).then((response) => response.text()).then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const intent = section.dataset.recommendationIntent;
        const candidates = $$('[data-product-recommendations]', html);
        const replacement = candidates.find((item) => item.dataset.recommendationIntent === intent);
        if (replacement) {
          section.innerHTML = replacement.innerHTML;
          section.hidden = replacement.hidden;
        }
      }).catch(() => {});
    });
  };

  const updateCollectionContextTitle = (section, contextTitle = '') => {
    if (!section) return;
    const baseTitle = section.dataset.collectionBaseTitle || '';
    const displayTitle = contextTitle || baseTitle;
    $$('[data-collection-title],[data-collection-breadcrumb]', section).forEach((node) => { node.textContent = displayTitle; });
    const parentBreadcrumb = $('[data-collection-parent-breadcrumb]', section);
    const parentSeparator = $('[data-collection-parent-separator]', section);
    const showParentBreadcrumb = Boolean(parentBreadcrumb && contextTitle && contextTitle !== baseTitle);
    if (parentBreadcrumb) parentBreadcrumb.hidden = !showParentBreadcrumb;
    if (parentSeparator) parentSeparator.hidden = !showParentBreadcrumb;
    const separator = document.title.includes('–') ? '–' : '—';
    const titleParts = document.title.split(separator);
    document.title = titleParts.length > 1 ? `${displayTitle} ${separator} ${titleParts.slice(1).join(separator).trim()}` : displayTitle;
  };

  const applyCollectionContext = () => {
    const section = $('[data-collection-section]');
    if (!section) return;
    const params = new URLSearchParams(window.location.search);
    const contextTitle = params.get('context') || '';
    const navTag = params.get('nav_tag');
    const saleOnly = params.get('sale_only') === 'true';
    updateCollectionContextTitle(section, contextTitle);

    $$('[data-sort-form],[data-filter-form]', section).forEach((form) => {
      [['context', contextTitle], ['nav_tag', navTag], ['sale_only', saleOnly ? 'true' : '']].forEach(([name, value]) => {
        const existing = $(`input[name="${name}"]`, form);
        if (!value) {
          existing?.remove();
          return;
        }
        if (existing) {
          existing.value = value;
          return;
        }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      });
    });

    if (!navTag && !saleOnly) return;

    if (navTag) {
      $$('.collection-edit-nav--context a,.filter-option--link', section).forEach((link) => {
        link.removeAttribute('aria-current');
        link.classList.remove('is-active');
      });
      $$(`a[href*="nav_tag=${encodeURIComponent(navTag)}"]`, section).forEach((link) => {
        link.setAttribute('aria-current', 'page');
        link.classList.add('is-active');
      });
    }

    const cards = $$('[data-product-card]', section);
    let visibleCount = 0;
    cards.forEach((card) => {
      const matchesTag = !navTag || (card.dataset.productTags || '').split('|').includes(navTag);
      const matchesSale = !saleOnly || card.dataset.productOnSale === 'true';
      const matches = matchesTag && matchesSale;
      card.hidden = !matches;
      if (matches) visibleCount += 1;
    });
    const count = $('.results-count', section);
    if (count) count.textContent = `${visibleCount} ${visibleCount === 1 ? 'product' : 'products'}`;
    if (!visibleCount) {
      const grid = $('[data-product-grid]', section);
      if (grid && !$('.empty-state', grid)) {
        const empty = document.createElement('div');
        empty.className = 'empty-state';
        empty.innerHTML = saleOnly
          ? '<p>No reduced pieces are available right now. Please check back for the next considered edit.</p><a class="btn btn--primary" href="/collections/all">Shop all products</a>'
          : `<p>No products are currently available in ${escapeHTML(contextTitle || navTag)}.</p><a class="btn btn--primary" href="${escapeHTML(window.location.pathname)}">View all products</a>`;
        grid.appendChild(empty);
      }
    }
    const pagination = $('.pagination', section);
    if (pagination) pagination.hidden = true;
  };

  const performPredictiveSearch = (input) => {
    const target = $('[data-predictive-search-results]');
    const term = input.value.trim();
    if (!target || !config.settings?.predictiveSearch || term.length < 2) { if (target) target.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
    predictiveController?.abort(); predictiveController = new AbortController();
    const query = new URLSearchParams({ q: term, section_id: 'predictive-search', 'resources[type]': 'product,collection,page,article,query', 'resources[limit]': '6', 'resources[limit_scope]': 'each', 'options[unavailable_products]': 'last' });
    fetch(`${config.routes.predictiveSearch}?${query}`, { signal: predictiveController.signal }).then((response) => response.text()).then((text) => {
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = $('#shopify-section-predictive-search', doc);
      target.innerHTML = content?.innerHTML || '';
      input.setAttribute('aria-expanded', String(Boolean(target.textContent.trim())));
    }).catch((error) => { if (error.name !== 'AbortError') target.innerHTML = ''; });
  };

  const activateSpaceTab = (requestedSpace, { updateHash = true, focus = false } = {}) => {
    const tabsRoot = $('[data-space-tabs]');
    if (!tabsRoot) return;
    const tabs = $$('[data-space-tab]', tabsRoot);
    const panels = $$('[data-space-panel]');
    const defaultSpace = tabs[0]?.dataset.spaceTab;
    const spaceName = tabs.some((tab) => tab.dataset.spaceTab === requestedSpace) ? requestedSpace : defaultSpace;
    if (!spaceName) return;
    tabs.forEach((tab) => {
      const selected = tab.dataset.spaceTab === spaceName;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) tab.focus();
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.spacePanel !== spaceName; });
    if (updateHash) {
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}#space-${spaceName}`);
    }
  };

  const activateSeasonalTab = (requestedSeason, { updateHash = true, focus = false } = {}) => {
    const tabsRoot = $('[data-seasonal-tabs]');
    if (!tabsRoot) return;
    const tabs = $$('[data-seasonal-tab]', tabsRoot);
    const panels = $$('[data-seasonal-panel]');
    const defaultSeason = tabs[0]?.dataset.seasonalTab;
    const seasonName = tabs.some((tab) => tab.dataset.seasonalTab === requestedSeason) ? requestedSeason : defaultSeason;
    if (!seasonName) return;
    tabs.forEach((tab) => {
      const selected = tab.dataset.seasonalTab === seasonName;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      if (selected && focus) {
        tab.focus();
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });
    panels.forEach((panel) => { panel.hidden = panel.dataset.seasonalPanel !== seasonName; });
    if (updateHash) {
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}#season-${seasonName}`);
    }
  };

  window.addEventListener('click', (event) => {
    const submitButton = event.target.closest?.('.jdgm-write-review-modal__nav-btn-next');
    const modal = submitButton?.closest('.jdgm-write-review-modal');
    const isIntroStep = modal?.querySelector(
      '.jdgm-write-review-modal__page--review-form-intro.current-page'
    );
    if (
      !submitButton
      || !modal
      || isIntroStep
      || !/^submit review$/i.test(submitButton.textContent.trim())
    ) return;
    window.setTimeout(() => setJudgeReviewSubmitting(submitButton), 0);
  }, true);

  document.addEventListener('submit', async (event) => {
    if (event.target.matches('[data-faq-search]')) { event.preventDefault(); return; }
    const form = event.target.closest('[data-add-to-cart]');
    if (!form) return;
    event.preventDefault();
    const button = $('[type="submit"]', form);
    const isCardQuickAdd = button?.classList.contains('quick-add');
    const originalButtonText = isCardQuickAdd ? button.textContent : '';
    button?.setAttribute('disabled', 'disabled');
    if (isCardQuickAdd) {
      button.textContent = 'Adding…';
      button.setAttribute('aria-busy', 'true');
    }
    try {
      const section = form.closest('[data-product-section]');
      const selectedVase = getSelectedVase(section);
      if (selectedVase) {
        const variantField = $('[data-variant-id]', form);
        const quantityField = $('[data-product-quantity]', form);
        const primaryQuantity = Math.max(1, Number(quantityField?.value || 1));
        const productTitle = section?.dataset.productTitle || 'Product';
        await addItems([
          { id: Number(variantField?.value || 0), quantity: primaryQuantity },
          { id: selectedVase.id, quantity: selectedVase.quantity, properties: { _floree_vase_addon: productTitle } }
        ].filter((item) => item.id));
        announce('Product and vase added to your cart.');
        return;
      }
      const response = await fetch(`${config.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) { const error = await response.json().catch(() => ({})); throw new Error(error.description || 'This item is unavailable.'); }
      if (form.closest('[data-quick-options]')) setLayerOpen(productModal, false, 'modal-open');
      await renderCart(await getCart()); setLayerOpen(cartShell, true, 'drawer-open'); announce('Added to your cart.');
    } catch (error) { announce(error.message || 'Unable to add this item.', 'error'); }
    finally {
      button?.removeAttribute('disabled');
      if (isCardQuickAdd) {
        button.textContent = originalButtonText;
        button.removeAttribute('aria-busy');
      }
    }
  });

  const visibleCampaignMediaSlides = (gallery) => $$('[data-campaign-media-slide]', gallery).filter((slide) => getComputedStyle(slide).display !== 'none');

  const syncCampaignMediaGallery = (gallery) => {
    const slides = visibleCampaignMediaSlides(gallery);
    if (!slides.length) return;
    const activeSlide = slides.reduce((nearest, slide) => (
      Math.abs(slide.offsetLeft - gallery.scrollLeft) < Math.abs(nearest.offsetLeft - gallery.scrollLeft) ? slide : nearest
    ), slides[0]);
    const activeIndex = slides.indexOf(activeSlide);
    const sourceIndex = Number(activeSlide.dataset.campaignMediaSlide || activeIndex);
    const frame = gallery.closest('.campaign-lab-featured-media');
    const label = $('[data-campaign-gallery-label]', frame);
    if (label) label.textContent = activeSlide.dataset.campaignMediaLabel || `Product view ${sourceIndex + 1}`;
    $$('[data-campaign-gallery-select]', frame).forEach((dot) => {
      const selected = Number(dot.dataset.campaignGallerySelect) === sourceIndex;
      dot.classList.toggle('is-active', selected);
      dot.setAttribute('aria-pressed', String(selected));
    });
    const previous = $('[data-campaign-gallery-step="-1"]', frame);
    const next = $('[data-campaign-gallery-step="1"]', frame);
    if (previous) previous.disabled = activeIndex === 0;
    if (next) next.disabled = activeIndex === slides.length - 1;
  };

  const selectCampaignMediaSlide = (target) => {
    const frame = target.closest('.campaign-lab-featured-media');
    const gallery = $('[data-campaign-featured-gallery]', frame);
    if (!gallery) return;
    const requestedIndex = Number(target.dataset.campaignGallerySelect || 0);
    const slide = $(`[data-campaign-media-slide="${requestedIndex}"]`, gallery);
    if (!slide || getComputedStyle(slide).display === 'none') return;
    gallery.scrollTo({ left: slide.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const stepCampaignMediaGallery = (target) => {
    const frame = target.closest('.campaign-lab-featured-media');
    const gallery = $('[data-campaign-featured-gallery]', frame);
    if (!gallery) return;
    const slides = visibleCampaignMediaSlides(gallery);
    const activeDot = $('.campaign-lab-featured-media__dot.is-active', frame);
    const activeSourceIndex = Number(activeDot?.dataset.campaignGallerySelect || 0);
    const activeIndex = Math.max(0, slides.findIndex((slide) => Number(slide.dataset.campaignMediaSlide) === activeSourceIndex));
    const nextIndex = Math.max(0, Math.min(slides.length - 1, activeIndex + Number(target.dataset.campaignGalleryStep || 1)));
    const nextSlide = slides[nextIndex];
    if (!nextSlide) return;
    gallery.scrollTo({ left: nextSlide.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  };

  const initCampaignLabReveals = () => {
    if (!document.body.classList.contains('campaign-lab-page')) return;
    const items = $$('.campaign-lab-story, .pdp-review-media, .campaign-lab-faq, .pdp-reviews, .campaign-lab-guarantee');
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    items.forEach((item) => item.classList.add('campaign-lab-reveal'));
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .12, rootMargin: '0px 0px -48px' });
    items.forEach((item) => observer.observe(item));
  };

  const initCampaignLabDepth = () => {
    if (!document.body.classList.contains('campaign-lab-page')) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (reduceMotion || !finePointer) return;

    $$('[data-campaign-depth-stage]').forEach((stage) => {
      if (stage.dataset.campaignDepthReady === 'true') return;
      stage.dataset.campaignDepthReady = 'true';
      let frame = null;

      const renderDepth = (event) => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const rect = stage.getBoundingClientRect();
          const relativeX = ((event.clientX - rect.left) / Math.max(rect.width, 1)) - .5;
          const relativeY = ((event.clientY - rect.top) / Math.max(rect.height, 1)) - .5;
          stage.style.setProperty('--campaign-depth-rotate-x', `${(-relativeY * 2.2).toFixed(2)}deg`);
          stage.style.setProperty('--campaign-depth-rotate-y', `${(relativeX * 2.6).toFixed(2)}deg`);
          frame = null;
        });
      };

      const resetDepth = () => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = null;
        stage.style.setProperty('--campaign-depth-rotate-x', '0deg');
        stage.style.setProperty('--campaign-depth-rotate-y', '0deg');
      };

      stage.addEventListener('pointermove', renderDepth, { passive: true });
      stage.addEventListener('pointerleave', resetDepth, { passive: true });
      stage.addEventListener('pointercancel', resetDepth, { passive: true });
    });
  };

  document.addEventListener('click', async (event) => {
    $$('details.filter-group[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
    $$('[data-sort-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.removeAttribute('open');
    });
    const target = event.target.closest('button,a');
    if (!target) return;
    if (target.matches('[data-studio-entry]')) {
      if (target.matches('[data-studio-set-seed]')) {
        try {
          const setNode = document.querySelector('[data-product-studio-set]');
          const setSeed = JSON.parse(setNode?.textContent || 'null');
          if (setSeed?.recipe?.id) {
            sessionStorage.setItem('floralsculpt_studio_set_seed_v1', JSON.stringify(setSeed));
          }
        } catch (_) {}
      }
      try {
        const studioUrl = new URL(target.href, document.baseURI);
        if (studioUrl.origin === window.location.origin) {
          studioUrl.searchParams.set('return_to', `${window.location.pathname}${window.location.search}${window.location.hash}`);
          target.href = studioUrl.href;
        }
      } catch (_) {}
    }
    if (target.matches('[data-open-cart]')) { event.preventDefault(); try { await renderCart(await getCart()); } catch (_) {} setLayerOpen(cartShell, true, 'drawer-open'); }
    if (target.matches('[data-close-cart]')) { event.preventDefault(); setLayerOpen(cartShell, false, 'drawer-open'); }
    if (target.matches('[data-open-search]')) { event.preventDefault(); setLayerOpen(searchShell, true, 'overlay-open'); }
    if (target.matches('[data-close-search]')) { event.preventDefault(); setLayerOpen(searchShell, false, 'overlay-open'); }
    if (
      target.matches('[data-menu-toggle]')
      && !target.closest('[data-site-header]')?.[Symbol.for('floralHero.navigation')]
    ) {
      event.preventDefault();
      toggleMobileMenu();
    }
    if (target.matches('[data-mobile-submenu-toggle]')) {
      event.preventDefault();
      const menu = target.closest('[data-mobile-nav]');
      const panel = document.getElementById(target.getAttribute('aria-controls'));
      const willOpen = target.getAttribute('aria-expanded') !== 'true';
      $$('[data-mobile-submenu-toggle][aria-expanded="true"]', menu).forEach((toggle) => {
        if (toggle === target) return;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', toggle.getAttribute('aria-label')?.replace(/^Hide /, 'Show ') || 'Show categories');
        const siblingPanel = document.getElementById(toggle.getAttribute('aria-controls'));
        if (siblingPanel) siblingPanel.hidden = true;
      });
      target.setAttribute('aria-expanded', String(willOpen));
      target.setAttribute('aria-label', target.getAttribute('aria-label')?.replace(willOpen ? /^Show / : /^Hide /, willOpen ? 'Hide ' : 'Show ') || `${willOpen ? 'Hide' : 'Show'} categories`);
      if (panel) panel.hidden = !willOpen;
    }
    if (target.matches('[data-space-tab]')) {
      event.preventDefault();
      activateSpaceTab(target.dataset.spaceTab, { focus: true });
    }
    if (target.matches('[data-seasonal-tab]')) {
      event.preventDefault();
      activateSeasonalTab(target.dataset.seasonalTab, { focus: true });
    }
    if (target.matches('[data-mega-trigger]')) {
      const menu = $(`[data-mega-menu="${CSS.escape(target.dataset.megaTrigger)}"]`);
      if (window.innerWidth >= 990 && menu && $('.mega-menu__heading, .mega-menu__feature:not(.mega-menu__feature--empty)', menu)) {
        event.preventDefault();
        openMega(target);
      }
    }
    if (target.matches('[data-open-quick-options]')) { event.preventDefault(); openQuickOptions(target); }
    if (target.matches('[data-cart-change]')) { event.preventDefault(); target.disabled = true; try { await changeCart(Number(target.dataset.line), Math.max(0, Number(target.dataset.quantity))); } catch (error) { announce(error.message, 'error'); } finally { target.disabled = false; } }
    if (target.matches('[data-vase-quantity-minus],[data-vase-quantity-plus]')) {
      event.preventDefault();
      const section = target.closest('[data-product-section]');
      const field = $('[data-vase-quantity]', target.closest('[data-vase-card]'));
      if (field) {
        const minimum = Math.max(1, Number(field.min || 1));
        const maximum = field.max ? Number(field.max) : Number.POSITIVE_INFINITY;
        const direction = target.matches('[data-vase-quantity-plus]') ? 1 : -1;
        field.value = String(Math.min(maximum, Math.max(minimum, Number(field.value || 1) + direction)));
        syncVaseSelector(section);
      }
    }
    if (target.matches('[data-quantity-minus],[data-quantity-plus]')) {
      const section = target.closest('[data-product-section]');
      const field = $('[data-product-quantity]', section);
      if (field) {
        const minimum = Math.max(1, Number(field.min || 1));
        const maximum = field.max ? Number(field.max) : Number.POSITIVE_INFINITY;
        field.value = String(Math.min(maximum, Math.max(minimum, Number(field.value || 1) + (target.matches('[data-quantity-plus]') ? 1 : -1))));
        updateProductQuantityPricing(section);
      }
    }
    if (target.matches('[data-submit-product-form]')) { event.preventDefault(); document.getElementById(target.dataset.submitProductForm)?.requestSubmit(); }
    if (target.matches('[data-wishlist-toggle]')) { event.preventDefault(); const handles = getWishlist(); const handle = target.dataset.wishlistToggle; setWishlist(handles.includes(handle) ? handles.filter((item) => item !== handle) : [...handles, handle]); announce(handles.includes(handle) ? 'Removed from wishlist.' : 'Saved to wishlist.'); if ($('[data-native-wishlist-page]')) renderWishlistPage(); }
    if (target.matches('[data-native-quick-add]')) { event.preventDefault(); if (!target.dataset.nativeQuickAdd) return; try { await addItems([{ id: Number(target.dataset.nativeQuickAdd), quantity: 1 }]); announce('Added to your cart.'); } catch (error) { announce(error.message, 'error'); } }
    if (target.matches('[data-add-look]')) {
      event.preventDefault();
      const section = target.closest('[data-product-section]');
      const quantityField = section ? $('[data-product-quantity]', section) : null;
      const setQuantity = Math.max(1, Number(quantityField?.value || 1));
      const selectedVase = getSelectedVase(section);
      const setLabel = selectedVase ? 'Florals + vase' : 'Florals only';
      const items = target.dataset.addLook.split(',').map((item) => {
        const [id, quantity] = item.split(':');
        return { id: Number(id), quantity: Number(quantity || 1) * setQuantity, properties: { _floree_look: setLabel } };
      }).filter((item) => item.id);
      if (selectedVase) {
        items.push({
          id: selectedVase.id,
          quantity: selectedVase.quantity,
          properties: { _floree_look: setLabel, _floree_vase_addon: section?.dataset.productTitle || 'Composition' }
        });
      }
      target.disabled = true;
      try {
        await addItems(items);
        announce(`${setQuantity} ${setQuantity === 1 ? 'composition' : 'compositions'} added to your cart.`);
      } catch (error) {
        announce(error.message, 'error');
      } finally {
        target.disabled = false;
      }
    }
    if (target.matches('[data-look-line-add]')) {
      event.preventDefault();
      const lookName = target.dataset.lookName || target.closest('[data-product-section]')?.dataset.productTitle || document.querySelector('h1')?.textContent?.trim() || 'Look';
      target.disabled = true;
      try {
        await addItems([{ id: Number(target.dataset.lookLineAdd), quantity: Math.max(1, Number(target.dataset.lookLineQuantity || 1)), properties: { _floree_look: lookName } }]);
        announce('Look item added to your cart.');
      } catch (error) {
        announce(error.message, 'error');
      } finally {
        target.disabled = false;
      }
    }
    if (target.matches('[data-finished-component-add]')) {
      event.preventDefault();
      target.disabled = true;
      try {
        await addItems([{
          id: Number(target.dataset.finishedComponentAdd),
          quantity: Math.max(1, Number(target.dataset.finishedComponentQuantity || 1))
        }]);
        announce('Botanical added to your cart.');
      } catch (error) {
        announce(error.message, 'error');
      } finally {
        target.disabled = false;
      }
    }
    if (target.matches('[data-look-preview]')) {
      event.preventDefault();
      const template = document.querySelector(`[data-look-preview-template="${CSS.escape(target.dataset.lookPreview)}"]`);
      const content = $('[data-product-modal-content]');
      if (template && content) {
        content.className = 'modal-card modal-card--look-preview';
        content.innerHTML = template.innerHTML;
        setLayerOpen(productModal, true, 'modal-open');
      }
    }
    if (target.matches('[data-look-save]')) {
      event.preventDefault();
      let savedLooks = [];
      try { savedLooks = JSON.parse(localStorage.getItem('floree.look-wishlist') || '[]'); } catch (_) {}
      const handle = target.dataset.lookSave;
      savedLooks = savedLooks.includes(handle) ? savedLooks.filter((item) => item !== handle) : [...savedLooks, handle];
      try { localStorage.setItem('floree.look-wishlist', JSON.stringify(savedLooks)); } catch (_) {}
      $$(`[data-look-save="${CSS.escape(handle)}"]`).forEach((item) => item.classList.toggle('is-saved', savedLooks.includes(handle)));
      announce(savedLooks.includes(handle) ? 'Look saved.' : 'Look removed from saved items.');
    }
    if (target.matches('[data-review-load-more]')) {
      event.preventDefault();
      const list = $('[data-real-review-list]');
      const total = Math.max(judgeReviewCache.length, Number(list?.dataset.reviewTotal || 0));
      visibleJudgeReviewCount = Math.min(total, visibleJudgeReviewCount + 6);
      const requestedJudgeReviewCount = visibleJudgeReviewCount;
      if (judgeReviewCache.length < requestedJudgeReviewCount) {
        const bridge = $('[data-judgeme-submission-bridge]');
        const nextPage = $('.jm-pagination__next:not([disabled]),.jdgm-paginate__next-page:not([disabled]),[aria-label="Next page"]:not([disabled])', bridge);
        if (nextPage) {
          target.disabled = true;
          target.classList.add('is-loading');
          nextPage.click();
          window.setTimeout(syncJudgeReviews, 700);
        } else {
          visibleJudgeReviewCount = judgeReviewCache.length;
          syncJudgeReviews();
        }
      } else {
        syncJudgeReviews();
      }
    }
    if (target.matches('[data-review-collapse]')) {
      event.preventDefault();
      visibleJudgeReviewCount = 6;
      syncJudgeReviews();
      $('[data-real-reviews-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (target.matches('[data-open-review-form]')) {
      event.preventDefault();
      openJudgeReviewForm();
    }
    if (target.matches('.jdgm-write-review-modal__close-btn,.jdgm-write-review-modal__nav-btn-close')) {
      window.setTimeout(closeJudgeReviewForm, 80);
    }
    if (target.matches('[data-review-media-strip-step]')) { event.preventDefault(); stepReviewMediaPreview(target); }
    if (target.matches('[data-open-review-detail],[data-open-review-gallery]')) { event.preventDefault(); openReviewDetail(target); }
    const vaseDetailsTrigger = target.closest?.('[data-open-vase-details]');
    if (vaseDetailsTrigger) { event.preventDefault(); openVaseDetails(vaseDetailsTrigger); }
    const vaseModalSelect = target.closest?.('[data-select-vase-from-modal]');
    if (vaseModalSelect) { event.preventDefault(); selectVaseFromModal(vaseModalSelect); }
    if (target.matches('[data-campaign-gallery-select]')) { event.preventDefault(); selectCampaignMediaSlide(target); }
    if (target.matches('[data-campaign-gallery-step]')) { event.preventDefault(); stepCampaignMediaGallery(target); }
    if (target.matches('[data-product-zoom]')) { event.preventDefault(); openProductGallery(target); }
    if (target.matches('[data-product-gallery-select]')) { event.preventDefault(); selectProductGalleryImage(target); }
    if (target.matches('[data-gallery-step]')) { event.preventDefault(); stepProductGallery(target); }
    if (target.matches('[data-close-product-modal]')) {
      event.preventDefault();
      setLayerOpen(productModal, false, 'modal-open');
      window.setTimeout(() => $('[data-product-modal-content]')?.classList.remove('modal-card--gallery', 'modal-card--review', 'modal-card--vase-detail'), 220);
    }
    if (target.matches('[data-filter-toggle]')) {
      const panel = $('[data-filter-panel]');
      const open = !panel?.classList.contains('is-open');
      const label = $('[data-filter-toggle-label]', target);
      panel?.classList.toggle('is-open', open);
      target.classList.toggle('is-open', open);
      target.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? 'Hide filters' : 'Filters';
      document.body.classList.toggle('filter-open', open);
    }
    if (target.matches('[data-filter-close]')) {
      const toggle = $('[data-filter-toggle]');
      $('[data-filter-panel]')?.classList.remove('is-open');
      toggle?.classList.remove('is-open');
      toggle?.setAttribute('aria-expanded', 'false');
      const label = toggle && $('[data-filter-toggle-label]', toggle);
      if (label) label.textContent = 'Filters';
      document.body.classList.remove('filter-open');
    }
    if (target.matches('[data-grid-density]')) { const density = target.dataset.gridDensity; $$('[data-product-grid]').forEach((grid) => grid.classList.toggle('is-compact', density === 'compact')); $$('[data-grid-density]').forEach((item) => item.setAttribute('aria-pressed', String(item === target))); try { localStorage.setItem('floree.grid-density', density); } catch (_) {} }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-auto-submit]')) event.target.form?.requestSubmit();
    if (event.target.matches('[data-filter-auto-submit]') && window.innerWidth >= 990) event.target.form?.requestSubmit();
    const section = event.target.closest('[data-product-section]');
    if (section && event.target.matches('[data-product-option] input')) updateVariant(section);
    if (event.target.matches('[data-vase-choice]')) {
      if (event.target.checked) {
        $$('[data-vase-choice]', section).forEach((choice) => {
          if (choice !== event.target) choice.checked = false;
        });
      }
      syncVaseSelector(section);
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-predictive-search-input]')) { window.clearTimeout(event.target._searchTimer); event.target._searchTimer = window.setTimeout(() => performPredictiveSearch(event.target), 180); }
    if (event.target.closest('[data-faq-search]')) { const query = event.target.value.trim().toLowerCase(); $$('[data-faq-item]').forEach((item) => { item.hidden = Boolean(query) && !item.dataset.searchText.includes(query); }); }
    if (event.target.matches('[data-product-quantity]')) updateProductQuantityPricing(event.target.closest('[data-product-section]'));
    if (event.target.matches('[data-vase-quantity]')) syncVaseSelector(event.target.closest('[data-product-section]'));
  });

  document.addEventListener('keydown', (event) => {
    trapFocus(event);
    const spaceTab = event.target.closest?.('[data-space-tab]');
    if (spaceTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = $$('[data-space-tab]', spaceTab.closest('[data-space-tabs]'));
      const currentIndex = tabs.indexOf(spaceTab);
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      activateSpaceTab(tabs[nextIndex].dataset.spaceTab, { focus: true });
      return;
    }
    const seasonalTab = event.target.closest?.('[data-seasonal-tab]');
    if (seasonalTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const tabs = $$('[data-seasonal-tab]', seasonalTab.closest('[data-seasonal-tabs]'));
      const currentIndex = tabs.indexOf(seasonalTab);
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabs.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      activateSeasonalTab(tabs[nextIndex].dataset.seasonalTab, { focus: true });
      return;
    }
    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && productModal?.classList.contains('is-open')) {
      const thumbnails = $$('[data-product-gallery-select]', productModal);
      const selectedIndex = thumbnails.findIndex((item) => item.classList.contains('is-selected'));
      if (thumbnails.length > 1 && selectedIndex >= 0) {
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        thumbnails[(selectedIndex + direction + thumbnails.length) % thumbnails.length].click();
      }
    }
    if (event.key === 'Escape') {
      if (productModal?.classList.contains('is-open')) {
        setLayerOpen(productModal, false, 'modal-open');
        window.setTimeout(() => $('[data-product-modal-content]')?.classList.remove('modal-card--gallery', 'modal-card--review', 'modal-card--vase-detail'), 220);
      }
      else if (cartShell?.classList.contains('is-open')) setLayerOpen(cartShell, false, 'drawer-open');
      else if (searchShell?.classList.contains('is-open')) setLayerOpen(searchShell, false, 'overlay-open');
      else if ($('[data-filter-panel]')?.classList.contains('is-open')) {
        const toggle = $('[data-filter-toggle]');
        $('[data-filter-panel]')?.classList.remove('is-open');
        toggle?.classList.remove('is-open');
        toggle?.setAttribute('aria-expanded', 'false');
        const label = toggle && $('[data-filter-toggle-label]', toggle);
        if (label) label.textContent = 'Filters';
        document.body.classList.remove('filter-open');
      }
      else if ($('details.filter-group[open]')) $$('details.filter-group[open]').forEach((details) => details.removeAttribute('open'));
      else if ($('[data-sort-menu][open]')) {
        const sortMenu = $('[data-sort-menu][open]');
        sortMenu?.removeAttribute('open');
        $('summary', sortMenu)?.focus();
      }
      else if ($('[data-site-header]')?.classList.contains('site-header--menu-open')) toggleMobileMenu();
      else closeMega();
    }
  });

  $$('[data-mega-item]').forEach((item) => {
    const trigger = $('[data-mega-trigger]', item);
    item.addEventListener('mouseenter', () => { if (trigger && window.innerWidth >= 990 && trigger.getAttribute('aria-expanded') !== 'true') openMega(trigger); });
  });
  $$('[data-mega-trigger]').forEach((trigger) => {
    trigger.addEventListener('focus', () => { if (window.innerWidth >= 990 && trigger.getAttribute('aria-expanded') !== 'true') openMega(trigger); });
  });
  const fallbackMobileMenu = $('[data-mobile-nav]');
  if (fallbackMobileMenu && !$('[data-site-header]')?.[Symbol.for('floralHero.navigation')]) {
    fallbackMobileMenu.inert = fallbackMobileMenu.getAttribute('aria-hidden') !== 'false';
  }
  $('[data-site-header]')?.addEventListener('mouseleave', () => window.setTimeout(() => { if (!$('[data-site-header]:hover')) closeMega(); }, 120));

  const savedDensity = (() => { try { return localStorage.getItem('floree.grid-density'); } catch (_) { return null; } })();
  if (savedDensity === 'compact') $('[data-grid-density="compact"]')?.click();
  let savedLooks = [];
  try { savedLooks = JSON.parse(localStorage.getItem('floree.look-wishlist') || '[]'); } catch (_) {}
  $$('[data-look-save]').forEach((item) => item.classList.toggle('is-saved', savedLooks.includes(item.dataset.lookSave)));
  $$('[data-product-section]').forEach((section) => {
    updateVariant(section);
    syncVaseSelector(section);
    initDeliveryEstimate(section);
  });
  window.addEventListener('pageshow', () => {
    $$('[data-product-section]').forEach(syncVaseSelector);
  });
  $$('[data-campaign-featured-gallery]').forEach((gallery) => {
    syncCampaignMediaGallery(gallery);
    gallery.addEventListener('scroll', () => {
      if (gallery._campaignMediaFrame) return;
      gallery._campaignMediaFrame = window.requestAnimationFrame(() => {
        gallery._campaignMediaFrame = null;
        syncCampaignMediaGallery(gallery);
      });
    }, { passive: true });
  });
  initCampaignLabDepth();
  initCampaignLabReveals();
  activateSpaceTab(window.location.hash.startsWith('#space-') ? window.location.hash.replace('#space-', '') : '', { updateHash: false });
  activateSeasonalTab(window.location.hash.startsWith('#season-') ? window.location.hash.replace('#season-', '') : '', { updateHash: false });
  window.addEventListener('hashchange', () => {
    activateSpaceTab(window.location.hash.startsWith('#space-') ? window.location.hash.replace('#space-', '') : '', { updateHash: false });
    activateSeasonalTab(window.location.hash.startsWith('#season-') ? window.location.hash.replace('#season-', '') : '', { updateHash: false });
  });
  syncWishlistButtons();
  renderWishlistPage();
  loadCompositionCardPrices();
  applyCollectionContext();
  loadRecommendations();
  watchJudgeReviews();
})();
